import { sleepAsync } from '../../libs/promise';

type Cache<Args, Rtn> = {
    args: Args;
    value: Promise<Rtn>;
};

function defaultCompareArgs(a: any[], b: any[]): boolean {
    return a.length === b.length && a.every((v, i) => v === b[i]);
}

type Options<Args extends any[]> = {
    compare?: (a: Args, b: Args) => boolean;
    delay?: number;
};

/**
 * 多重実行を防止する目的の関数ラッパー
 * 一度呼び出された後に処理が完了する前に同じ引数で再度呼び出された場合は多重実行せずに未完了のPromiseを返す
 *
 * @param fn 多重実行させたくない非同期関数 thisが必要な場合はアロー関数式として定義するか、bindして渡すこと
 * @param options.compare 引数の同一性を判定する関数
 * @param options.delay 指定した時間は完了後もキャッシュを返す
 */
export function cacheAsync<Args extends any[], Rtn>(
    fn: (...args: Args) => Promise<Rtn>,
    options?: Options<Args>
): (...args: Args) => Promise<Rtn> {
    const delay = options?.delay;
    const compare = options?.compare ?? defaultCompareArgs;
    const cachesPerArgs = new Set<Cache<Args, Rtn>>();
    return (...args: Args): Promise<Rtn> => {
        const exists = [...cachesPerArgs].find((c) => compare(c.args, args));
        if (exists) return exists.value;
        const r = fn(...args);
        if (r instanceof Promise) {
            const cache = {
                args,
                value: r.finally(async () => {
                    if (delay) await sleepAsync(delay);
                    cachesPerArgs.delete(cache);
                }) as Promise<Rtn>,
            };
            cachesPerArgs.add(cache);
        }
        return r;
    };
}

/**
 * Promiseを返すメソッドを対象にとるデコレータ
 * メソッドが一度呼び出された後に処理が完了する前に同じ引数で再度呼び出された場合は多重実行せずに未完了のPromiseを返す
 *
 * @param options.compare 引数の同一性を判定する関数
 * @param options.delay 指定した時間は完了後もキャッシュを返す
 */
export function CacheAsync<Args extends any[]>(options?: Options<Args>) {
    return (target: any, key: string, descriptor: PropertyDescriptor) => {
        const cachesPerInstance = new WeakMap<object, Function>();
        const fn = descriptor.value;
        descriptor.value = function (...args: any[]) {
            const cachedFunction =
                cachesPerInstance.get(this) ||
                cacheAsync(fn.bind(this), options);
            cachesPerInstance.set(this, cachedFunction);
            return cachedFunction(...args);
        };
    };
}
